import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Loader2, Check, BarChart3, AlertCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";

export default function InteractivePoll({ pollId, className = "", scrollableOptions = false }) {
  const { user } = useAuth();
  const { theme } = useTheme();
  const isAdmin = user?.role === "admin";
  // Panel background image — opacity slider is the image's own visibility:
  // 100% = image fully shown, 0% = fully washed out to white. Same
  // linear-gradient-over-image trick as the mobile menu drawer.
  const panelBgImage = (theme.poll_panel_bg_image || "").trim();
  const panelOpacity = Math.max(0, Math.min(100, Number(theme.poll_panel_opacity ?? 100))) / 100;
  const panelStyle = panelBgImage
    ? {
        backgroundImage: `linear-gradient(rgba(255,255,255,${1 - panelOpacity}), rgba(255,255,255,${1 - panelOpacity})), url("${panelBgImage}")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }
    : {};
  const [poll, setPoll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(false);
  const [error, setError] = useState("");
  const [voted, setVoted] = useState(false);

  const loadPoll = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/polls/${pollId}`);
      setPoll(r.data);
      // Check if voted in local storage
      const hasVoted = localStorage.getItem(`voted_poll_${pollId}`) === "true";
      setVoted(hasVoted);
    } catch (e) {
      console.error(e);
      setError("Unable to load poll.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (pollId) {
      loadPoll();
    }
  }, [pollId]);

  const handleVote = async (optionId) => {
    if (voting || voted || !poll || poll.closed) return;
    setVoting(true);
    setError("");
    try {
      const r = await api.post(`/polls/${pollId}/vote?option_id=${optionId}`);
      setPoll(r.data);
      localStorage.setItem(`voted_poll_${pollId}`, "true");
      setVoted(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Voting failed.");
    } finally {
      setVoting(false);
    }
  };

  if (loading) {
    return (
      <div className={`rounded-md border border-black/10 bg-white p-6 shadow-sm flex flex-col items-center justify-center min-h-[180px] ${className}`}>
        <Loader2 className="h-6 w-6 animate-spin text-[#6CABDD]" />
        <span className="mt-2 text-xs font-semibold text-[var(--brand-body)]">Loading Poll...</span>
      </div>
    );
  }

  if (error && !poll) {
    return (
      <div className={`rounded-md border border-[#E63946]/20 bg-[#E63946]/5 p-6 text-center ${className}`}>
        <AlertCircle className="mx-auto h-6 w-6 text-[#E63946] mb-2" />
        <p className="text-sm font-semibold text-[var(--brand-ink)]">{error}</p>
      </div>
    );
  }

  if (!poll) return null;

  const totalVotes = (poll.options || []).reduce((acc, curr) => acc + (curr.votes || 0), 0);
  const showResults = voted || poll.closed;

  return (
    <div
      className={`rounded-md border border-black/10 bg-white p-6 shadow-sm ${className}`}
      style={panelStyle}
      data-testid={`poll-box-${pollId}`}
    >
      <div className="flex items-center gap-1.5 text-brand-accent mb-2">
        <BarChart3 size={16} strokeWidth={2.5} />
        <span className="label-kicker">{poll.closed ? "Poll Closed" : voted ? "Voted" : "Active Fan Poll"}</span>
      </div>
      
      <h3 className="font-display text-base sm:text-lg font-black leading-tight text-[var(--brand-ink)]">
        {poll.question}
      </h3>

      {error ? (
        <p className="mt-3 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-1.5 text-xs text-[var(--brand-ink)]">
          {error}
        </p>
      ) : null}

      <div className={`mt-4 space-y-3 ${scrollableOptions ? "max-h-72 overflow-y-auto pr-1 scrollbar-thin" : ""}`}>
        {poll.options.map((opt) => {
          const votesCount = opt.votes || 0;
          const percentage = totalVotes > 0 ? Math.round((votesCount / totalVotes) * 100) : 0;

          if (showResults) {
            return (
              <div key={opt.id} className={`relative overflow-hidden rounded-md border border-black/5 p-3.5 flex flex-col justify-center ${panelBgImage ? "bg-[#F8FAFC]/85 backdrop-blur-sm" : "bg-[#F8FAFC]"}`}>
                {/* Visual percentage progress bar */}
                <div
                  className="absolute inset-y-0 left-0 bg-[#6CABDD]/10 transition-all duration-700 ease-out"
                  style={{ width: `${percentage}%` }}
                />
                <div className="relative z-10 flex items-center justify-between gap-4 text-xs sm:text-sm font-semibold">
                  <span className="text-[var(--brand-ink)] truncate flex items-center gap-1.5">
                    {opt.text}
                  </span>
                  <span className="shrink-0 text-[var(--brand-body)] font-display font-black" data-testid={`poll-option-result-${opt.id}`}>
                    {percentage}%{isAdmin ? ` (${votesCount})` : ""}
                  </span>
                </div>
              </div>
            );
          }

          return (
            <button
              key={opt.id}
              type="button"
              disabled={voting}
              onClick={() => handleVote(opt.id)}
              className={`w-full text-left card-hover group relative overflow-hidden rounded-md border border-black/10 p-3.5 transition flex items-center justify-between hover:bg-[#6CABDD]/10 ${panelBgImage ? "bg-white/85 backdrop-blur-sm" : "bg-white"}`}
              data-testid={`poll-vote-${opt.id}`}
            >
              <span className="font-display text-xs sm:text-sm font-bold text-[var(--brand-ink)] group-hover:text-[#002d75]">
                {opt.text}
              </span>
              <span className="h-5 w-5 rounded-full border border-black/15 flex items-center justify-center bg-white group-hover:border-[#002d75] shrink-0" />
            </button>
          );
        })}
      </div>

      <div className="mt-4 border-t border-black/5 pt-3 flex items-center justify-between text-[11px] text-[var(--brand-body)] font-bold uppercase tracking-wider">
        <span data-testid="poll-total-votes">{isAdmin ? `Total Votes: ${totalVotes}` : "Poll Results"}</span>
        {poll.closed ? (
          <span className="text-red-500 font-black">Voting Closed</span>
        ) : voted ? (
          <span className="text-emerald-600 font-black flex items-center gap-1">✓ Thank you</span>
        ) : null}
      </div>
    </div>
  );
}
